import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider, keepPreviousData } from "@tanstack/react-query";
import { AuthProvider } from "@/contexts/AuthContext";
import App from "./App";
import "./index.css";

// Remove initial loader once React takes over
const initialLoader = document.getElementById('initial-loader');
if (initialLoader) {
  initialLoader.remove();
}

// Initialize performance monitoring (deferred)
import('./lib/performanceMonitoring').then(({ observeWebVitals }) => {
  if ('requestIdleCallback' in window) {
    (window as Window & { requestIdleCallback: (cb: () => void) => void }).requestIdleCallback(observeWebVitals);
  } else {
    setTimeout(observeWebVitals, 100);
  }
});

// Optimized QueryClient configuration for performance
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes - reduce refetches
      gcTime: 1000 * 60 * 30, // 30 minutes garbage collection
      retry: 1, // Single retry for faster failure
      refetchOnWindowFocus: false, // Disable focus refetch
      refetchOnMount: false, // Use cache on mount when available
      refetchOnReconnect: 'always', // But always refetch on reconnect
      // Keep previous results visible while new query loads — eliminates
      // skeleton flashes when paginating, filtering, or sorting.
      placeholderData: keepPreviousData,
    },
  },
});

// Provider stack at TRUE root: QueryClient -> Auth -> App
createRoot(document.getElementById("root")!).render(
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <App />
    </AuthProvider>
  </QueryClientProvider>
);

// Service-worker cleanup.
// A previous version of this app shipped a caching service worker that served
// stale JS/CSS chunks after deployments. We now ship a kill-switch SW at
// /sw.js that unregisters itself. We also defensively unregister any leftover
// registrations so users don't have to hard-refresh.
if ('serviceWorker' in navigator) {
  navigator.serviceWorker
    .getRegistrations()
    .then((regs) => regs.forEach((r) => r.unregister().catch(() => {})))
    .catch(() => {});
  // Wipe Cache Storage entries the old SW may have populated.
  if ('caches' in window) {
    caches
      .keys()
      .then((keys) => Promise.all(keys.map((k) => caches.delete(k))))
      .catch(() => {});
  }
}

// Stale chunk recovery: after a deploy, an open tab still holds references to
// old hashed chunks that no longer exist on the server. Detect those failures
// and reload once. Guard with sessionStorage so we don't hit a refresh loop.
const CHUNK_RELOAD_KEY = '__anw_chunk_reload__';
const isChunkLoadError = (msg: string) =>
  /Loading chunk [\d]+ failed/i.test(msg) ||
  /ChunkLoadError/i.test(msg) ||
  /Failed to fetch dynamically imported module/i.test(msg) ||
  /Importing a module script failed/i.test(msg);

const handleChunkError = (msg: string) => {
  if (!isChunkLoadError(msg)) return;
  if (sessionStorage.getItem(CHUNK_RELOAD_KEY)) return;
  sessionStorage.setItem(CHUNK_RELOAD_KEY, '1');
  // Tiny delay so the user sees that something is happening.
  setTimeout(() => window.location.reload(), 50);
};

window.addEventListener('error', (e) => handleChunkError(String(e?.message || '')));
window.addEventListener('unhandledrejection', (e) => {
  const reason = e?.reason;
  const msg = typeof reason === 'string' ? reason : reason?.message || '';
  handleChunkError(String(msg));
});
// Clear the guard once the app has booted successfully.
window.addEventListener('load', () => {
  setTimeout(() => sessionStorage.removeItem(CHUNK_RELOAD_KEY), 5000);
});
